<template>
  <FriedModal :size="modalSize" @close="$emit('close')">
    <template v-if="showChangeSignerSelectSection" #container>
      <FriedModalSubContainer v-if="transfered">
        <template #header>
          <FriedModalHeader
            :header="$t('change_signer_success_title')"
            :description="$t('change_signer_success_description')"
          />
        </template>
        <template #footer>
          <FriedModalButtonsFooter>
            <FriedButton data-external="invite-modal-close-btn" @click="$emit('close')">
              {{ $t('cc_got_it_button') }}
            </FriedButton>
          </FriedModalButtonsFooter>
        </template>
      </FriedModalSubContainer>
      <FriedModalSubContainer v-else>
        <template #header>
          <FriedModalHeader
            :header="$t('change_signer_title')"
            :description="$t('change_signer_description')"
          />
        </template>
        <div class="change-signer-container">
          <FriedSelect
            v-model="selectedRecipient"
            data-external="change-signer-select"
            :options="recipientOptions"
            fixed-label
            searchable
            option-label="fullName"
            :label="$t('recipients')"
            :placeholder="$t('change_signer_placeholder', { count: recipients.length })"
            @search="searchInput = $event"
          >
            <template v-if="!!searchInput" #customBottomOption>
              <FriedSelectOption @click="handleAddFromSearch">
                <FriedIcon icon="user-add" />
                {{ $t('add_new_recipient_label', { name: searchInput }) }}
              </FriedSelectOption>
            </template>
            <template #emptyState><span /></template>
          </FriedSelect>
          <FriedParagraph>
            {{ $t('change_signer_help_text') }}
            <FriedLink @click="showChangeSignerSelectSection = false">
              {{ $t('change_signer_help_text_link') }}
            </FriedLink>
          </FriedParagraph>
        </div>
        <template #footer>
          <FriedModalButtonsFooter>
            <FriedButton
              data-external="tranfer-cancel-button"
              button-type="secondary"
              :disabled="transfering"
              @click="$emit('close')"
            >
              {{ $t('cancel') }}
            </FriedButton>
            <FriedButton
              :loading="transfering"
              :disabled="!selectedRecipient"
              data-external="invite-modal-close-btn"
              @click="$emit('transfer', { ...selectedRecipient, transferSignature: true })"
            >
              {{ $t('change_signer') }}
            </FriedButton>
          </FriedModalButtonsFooter>
        </template>
      </FriedModalSubContainer>
    </template>
    <template v-else #container>
      <FriedModalSubContainer v-if="invited">
        <template #header>
          <FriedModalHeader
            :header="$t('invite_sent_title')"
            :description="$t('invite_sent_description', { firstName: selectedRecipient.firstName })"
          />
        </template>
        <template #footer>
          <FriedModalButtonsFooter>
            <FriedButton data-external="invite-modal-close-btn" @click="$emit('close')">
              {{ $t('close') }}
            </FriedButton>
          </FriedModalButtonsFooter>
        </template>
      </FriedModalSubContainer>
      <InviteRecipientForm
        v-else
        :recipient="selectedRecipient"
        :recipients="recipients"
        :loading="inviting"
        :is-signer="isSigner"
        :show-role-transfer="showRoleTransfer"
        :has-signature-element="hasSignatureElement"
        :is-change-signer="isChangeSigner"
        @invite="invite"
        @transfer="$emit('transfer', $event)"
        @transfer-to-existing="handleTransferToExisting"
        @close="$emit('close')"
      />
    </template>
  </FriedModal>
</template>

<script lang="ts">
import type {
  SigningPageRecipient,
  InviteRecipientData,
} from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { PropType } from 'vue';
import { defineComponent, ref, computed } from 'vue';
import { useAsyncState } from '@vueuse/core';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { DocumentService } from '../../api/documents/services/document.service';
import { useRecipientSearch } from '../composables/recipient-search.composable';
import InviteRecipientForm from './InviteRecipientForm.vue';

export default defineComponent({
  components: { InviteRecipientForm },
  props: {
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      default: () => [],
    },
    transfering: Boolean,
    transfered: Boolean,
    isInviteEnabled: Boolean,
    isSigned: Boolean,
    isSigningForward: Boolean,
    isChangeSigner: Boolean,
  },
  emits: ['success', 'transfer', 'close'],
  setup(props) {
    const recipientOptions = computed(() =>
      props.recipients.map(recipient => ({
        firstName: recipient.firstName,
        lastName: recipient.lastName,
        email: recipient.email,
        mobile: recipient.mobile,
        title: recipient.title,
        companyName: recipient.companyName,
        companyNumber: recipient.companyNumber,
        disabled: recipient.role !== RecipientRole.CC,
        tooltip: recipient.role !== RecipientRole.CC ? t('change_signer_disabled_tooltip') : '',
        fullName:
          RecipientsHelper.getRecipientFullName(recipient) || recipient.email || recipient.mobile,
      }))
    );

    const hasViewers = computed(() =>
      props.recipients.some(recipient => recipient.role === RecipientRole.CC)
    );
    const showChangeSignerSelectSection = ref(props.isChangeSigner && hasViewers.value);

    const isSigner = computed(() => props.recipient.role === RecipientRole.Signer);
    const hasSignatureElement = computed(() => !!props.recipient?.signature);

    const showRoleTransfer = computed(
      () => isSigner.value && !props.isSigned && props.isSigningForward
    );

    const selectedRecipient = ref<SigningPageRecipient>();

    const searchInput = ref('');
    const { data: recipientDataFromSearch } = useRecipientSearch(searchInput);

    const handleAddFromSearch = () => {
      selectedRecipient.value = {
        ...selectedRecipient.value,
        ...recipientDataFromSearch.value,
      };
      showChangeSignerSelectSection.value = false;
    };

    const handleTransferToExisting = (recipient: SigningPageRecipient) => {
      selectedRecipient.value = recipient;
      showChangeSignerSelectSection.value = true;
    };

    const {
      isLoading: inviting,
      isReady: invited,
      execute: invite,
    } = useAsyncState((payload: InviteRecipientData) => DocumentService.invite(payload), null, {
      immediate: false,
    });

    const modalSize = computed(() =>
      invited.value || showChangeSignerSelectSection.value ? 'small' : 'large'
    );

    return {
      inviting,
      invited,
      invite,
      isSigner,
      hasSignatureElement,
      selectedRecipient,
      showRoleTransfer,
      modalSize,
      recipientOptions,
      searchInput,
      showChangeSignerSelectSection,
      handleAddFromSearch,
      handleTransferToExisting,
    };
  },
});
</script>
<style lang="scss" scoped>
.change-signer-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-50);
}
</style>
